import jsPDF from "jspdf";
import { FIN, sensitivityProfit, SENS_PRICES, SENS_LAND, IMAGES, CONTENT } from "../content";

const BG = "#0d0f0e";
const GOLD = "#d4af37";
const CREAM = "#f2f0e9";
const MUTED = "#a1a3a1";

const W = 297, H = 210, M = 20;

let RTL = false, SERIF = "times", SANS = "helvetica";
const TX = () => (RTL ? W - M : M);
const AL = () => (RTL ? "right" : "left");

let AR_FONT = null;
const loadArabicFont = async () => {
  if (AR_FONT) return AR_FONT;
  try {
    const res = await fetch("https://cdn.jsdelivr.net/gh/google/fonts@main/ofl/amiri/Amiri-Regular.ttf");
    if (!res.ok) return null;
    const bytes = new Uint8Array(await res.arrayBuffer());
    let bin = "";
    for (let i = 0; i < bytes.length; i += 0x8000) {
      bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
    }
    AR_FONT = btoa(bin);
    return AR_FONT;
  } catch {
    return null;
  }
};

const loadImage = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth;
        c.height = img.naturalHeight;
        c.getContext("2d").drawImage(img, 0, 0);
        resolve(c.toDataURL("image/jpeg", 0.72));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });

const page = (doc, first = false) => {
  if (!first) doc.addPage();
  doc.setFillColor(BG);
  doc.rect(0, 0, W, H, "F");
};

const kicker = (doc, text, y = M + 4) => {
  doc.setFont(SANS, "normal");
  doc.setFontSize(9);
  doc.setTextColor(GOLD);
  doc.text(text.toUpperCase(), TX(), y, RTL ? { align: "right" } : { charSpace: 1.5 });
};

const title = (doc, text, y = M + 20, size = 30) => {
  doc.setFont(SERIF, "normal");
  doc.setFontSize(size);
  doc.setTextColor(CREAM);
  const lines = doc.splitTextToSize(text.replace(/\n/g, " "), W - 2 * M);
  doc.text(lines, TX(), y, { align: AL() });
  return y + lines.length * (size * 0.42);
};

const body = (doc, text, y, maxW = W - 2 * M) => {
  doc.setFont(SANS, "normal");
  doc.setFontSize(11);
  doc.setTextColor(MUTED);
  const lines = doc.splitTextToSize(text, maxW);
  doc.text(lines, TX(), y, { align: AL() });
  return y + lines.length * 5.5;
};

const footer = (doc, t, num) => {
  doc.setFontSize(7.5);
  doc.setTextColor("#6b6d6a");
  doc.setFont(SANS, "normal");
  doc.text(t.footer, TX(), H - 8, { align: AL() });
  doc.setTextColor(GOLD);
  doc.text(String(num).padStart(2, "0"), RTL ? M : W - M, H - 8, { align: RTL ? "left" : "right" });
};

export async function exportDeckPdf(t, lang, unlocked) {
  RTL = lang === "ar";
  SERIF = "times";
  SANS = "helvetica";
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  if (RTL) {
    const b64 = await loadArabicFont();
    if (b64) {
      doc.addFileToVFS("Amiri-Regular.ttf", b64);
      doc.addFont("Amiri-Regular.ttf", "Amiri", "normal");
      SERIF = "Amiri";
      SANS = "Amiri";
    } else {
      t = CONTENT.en;
      lang = "en";
      RTL = false;
    }
  }
  let num = 1;

  const imgs = {};
  const entries = await Promise.all(
    Object.entries(IMAGES).map(async ([k, url]) => [k, await loadImage(url)])
  );
  entries.forEach(([k, v]) => (imgs[k] = v));

  // Cover
  page(doc, true);
  if (imgs.hero) {
    doc.addImage(imgs.hero, "JPEG", 0, 0, W, H);
    doc.setFillColor(10, 12, 11);
    doc.setGState(new doc.GState({ opacity: 0.55 }));
    doc.rect(0, 0, W, H, "F");
    doc.setGState(new doc.GState({ opacity: 1 }));
  }
  doc.setFont(SERIF, "normal");
  doc.setFontSize(64);
  doc.setTextColor(CREAM);
  doc.text(`${t.cover.title1} & ${t.cover.title2}`, TX(), H / 2 + 8, { align: AL() });
  doc.setFont(SANS, "normal");
  doc.setFontSize(12);
  doc.setTextColor(GOLD);
  doc.text(t.cover.kicker.toUpperCase(), TX(), H / 2 - 14, RTL ? { align: "right" } : { charSpace: 1.5 });
  doc.setFontSize(13);
  doc.setTextColor(CREAM);
  doc.text(t.cover.subtitle, TX(), H / 2 + 22, { align: AL() });
  footer(doc, t, num++);

  const sections = [
    { s: t.location, img: imgs.golf, stats: t.location.stats.map((x) => `${x.v} — ${x.l}`) },
    { s: t.project, img: imgs.night, stats: t.project.points },
    { s: t.lifestyle, img: imgs.pool, stats: t.lifestyle.items.map((x) => `${x.t} — ${x.d}`) },
    { s: t.villa, img: imgs.terrace, stats: t.villa.specs.map((x) => `${x.v} ${x.u} — ${x.l}`) },
    { s: t.custom, img: imgs.bedroom, stats: t.custom.items },
    { s: t.masterplan, img: imgs.aerial, stats: t.masterplan.stats.map((x) => `${x.v} — ${x.l}`) },
  ];

  sections.forEach(({ s, img, stats }) => {
    page(doc);
    kicker(doc, `${s.no} — ${s.label}`);
    let y = title(doc, s.title, M + 22, 26);
    y = body(doc, s.body, y + 6, img ? W / 2 - M - 6 : W - 2 * M);
    y += 6;
    doc.setFontSize(10);
    stats.forEach((line) => {
      doc.setTextColor(GOLD);
      doc.text("—", TX(), y, { align: AL() });
      doc.setTextColor(CREAM);
      const bx = RTL ? W - M - 7 : M + 7;
      doc.text(doc.splitTextToSize(line, W / 2 - M - 14), bx, y, { align: AL() });
      y += 7;
    });
    if (img) {
      const ix = RTL ? M : W / 2 + 4;
      doc.addImage(img, "JPEG", ix, M + 26, W / 2 - M - 4, ((W / 2 - M - 4) * 2) / 3);
    }
    footer(doc, t, num++);
  });

  // Contact
  page(doc);
  kicker(doc, `${t.contact.no} — ${t.contact.label}`);
  let y = title(doc, t.contact.title, M + 24, 30);
  body(doc, t.contact.body, y + 8);
  footer(doc, t, num++);

  if (unlocked) {
    // Investment case
    page(doc);
    kicker(doc, t.invest.label);
    y = title(doc, t.invest.title, M + 24, 30);
    y = body(doc, t.invest.disclaimer, y + 6);
    y += 8;
    const colW = (W - 2 * M) / 3;
    t.invest.kpis.forEach((k, i) => {
      const x = M + (i % 3) * colW;
      const yy = y + Math.floor(i / 3) * 28;
      doc.setDrawColor(60, 62, 60);
      doc.rect(x, yy, colW - 4, 24);
      doc.setFont(SERIF, "normal");
      doc.setFontSize(18);
      doc.setTextColor(GOLD);
      const kx = RTL ? x + colW - 9 : x + 5;
      doc.text(k.v, kx, yy + 10, { align: AL() });
      doc.setFont(SANS, "normal");
      doc.setFontSize(8);
      doc.setTextColor(MUTED);
      doc.text(doc.splitTextToSize(k.l, colW - 12), kx, yy + 16, { align: AL() });
    });
    footer(doc, t, num++);

    // Cost breakdown page
    page(doc);
    kicker(doc, t.invest.waterfallTitle);
    y = M + 20;
    doc.setFont(SERIF, "normal");
    doc.setFontSize(22);
    doc.setTextColor(CREAM);
    doc.text(t.invest.waterfallTitle, TX(), y, { align: AL() });
    y += 14;
    doc.setFont(SANS, "normal");
    doc.setFontSize(11);
    const rows = [
      [lang === "it" ? "Ricavi (GDV)" : lang === "ar" ? "الإيرادات (GDV)" : "Revenues (GDV)", `€${FIN.gdv}M`],
      ...FIN.costs.map((c) => [`− ${c[lang]}`, `€${c.value}M`]),
      [lang === "it" ? "Costo totale" : lang === "ar" ? "التكلفة الإجمالية" : "Total cost", `€${FIN.totalCost}M`],
      [lang === "it" ? "Utile lordo di sviluppo" : lang === "ar" ? "إجمالي ربح التطوير" : "Gross development profit", `€${FIN.profit}M`],
    ];
    rows.forEach(([l, v], i) => {
      const strong = i === 0 || i >= rows.length - 2;
      doc.setTextColor(strong ? GOLD : MUTED);
      doc.text(l, TX(), y, { align: AL() });
      doc.setTextColor(strong ? GOLD : CREAM);
      doc.text(v, RTL ? M + 30 : W / 2, y, { align: RTL ? "left" : "right" });
      y += 8;
    });
    y = body(doc, t.invest.waterfallNote, y + 4, W / 2 - M);
    body(doc, `${t.invest.taxNote}\n\n${t.invest.alignNote}`, y + 6, W / 2 - M);

    // Sensitivity on opposite side
    let sx = RTL ? M : W / 2 + 14, sy = M + 20;
    doc.setFont(SERIF, "normal");
    doc.setFontSize(16);
    doc.setTextColor(CREAM);
    doc.text(t.invest.sensTitle, RTL ? W / 2 - 14 : sx, sy, { align: AL() });
    sy += 10;
    doc.setFont(SANS, "normal");
    doc.setFontSize(9);
    const cw = 24;
    doc.setTextColor(MUTED);
    SENS_PRICES.forEach((p, i) => doc.text(`€${p.toFixed(1)}M`, sx + 26 + i * cw, sy));
    sy += 7;
    SENS_LAND.forEach((l) => {
      doc.setTextColor(MUTED);
      doc.text(`€${l}M`, sx, sy);
      SENS_PRICES.forEach((p, i) => {
        const base = p === 5 && l === 5;
        doc.setTextColor(base ? GOLD : CREAM);
        doc.text(String(sensitivityProfit(p, l).toFixed(1)), sx + 26 + i * cw, sy);
      });
      sy += 7;
    });
    sy += 6;
    doc.setFontSize(8);
    doc.setTextColor(MUTED);
    doc.text(doc.splitTextToSize(t.invest.sensNote, W / 2 - M - 14), sx, sy);
    footer(doc, t, num++);

    // Risk page
    page(doc);
    kicker(doc, t.invest.riskTitle);
    y = title(doc, t.invest.riskTitle, M + 24, 26);
    y = body(doc, t.invest.riskBody, y + 6);
    y += 8;
    t.invest.riskScenarios.forEach((s) => {
      doc.setFont(SERIF, "normal");
      doc.setFontSize(15);
      doc.setTextColor(GOLD);
      doc.text(s.t, TX(), y, { align: AL() });
      y += 7;
      y = body(doc, s.d, y);
      y += 6;
    });
    footer(doc, t, num++);
  }

  doc.save(`golf-and-sea-is-molas-${lang}${unlocked ? "-investor" : ""}.pdf`);
}
